import { gql, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { PdfDoc } from "./pdfDoc";
import { pdfBlob } from "./renderPdf";

const REPORT_AUTHOR = gql`
  query reportAuthor {
    me {
      id
      firstName
      lastName
    }
  }
`;

/** The browser remembers who prepares the reports; it is a convenience, so a blocked storage is fine. */
const PREPARED_BY_KEY = "ilbp.reports.preparedBy";

function remembered(): string | null {
  try {
    return window.localStorage.getItem(PREPARED_BY_KEY);
  } catch {
    return null;
  }
}

/** "Prepared by": what this browser last used, else the signed-in admin's name. */
export function usePreparedBy(): [string, (next: string) => void] {
  const [value, setValue] = useState<string | null>(remembered);
  const { data } = useQuery(REPORT_AUTHOR, { skip: value !== null, fetchPolicy: "cache-first" });
  const fromAccount = data?.me ? `${data.me.firstName ?? ""} ${data.me.lastName ?? ""}`.trim() : "";
  const set = (next: string) => {
    setValue(next);
    try {
      window.localStorage.setItem(PREPARED_BY_KEY, next);
    } catch {
      // Not remembered, and that is all.
    }
  };
  return [value ?? fromAccount, set];
}

export const PreparedByField: React.FC<{ value: string; onChange: (next: string) => void }> = ({ value, onChange }) => (
  <TextField label="Prepared by" size="small" value={value} onChange={(event) => onChange(event.target.value)} fullWidth />
);

export const OptionCheckbox: React.FC<{ value: boolean; onChange: (next: boolean) => void; label: string }> = ({ value, onChange, label }) => (
  <FormControlLabel control={<Checkbox checked={value} onChange={(event) => onChange(event.target.checked)} />} label={label} />
);

export const MEANINGS_LABEL = "Say what each number means";

/** How long typing pauses before the preview is drawn again. */
const REDRAW_MS = 400;

/**
 * The export: the options on the left, and on the right the PDF itself, drawn
 * again a moment after each change, so what is previewed is the file that is
 * saved. `doc` is null while the report is still loading.
 */
export const ReportPdfDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  doc: PdfDoc | null;
  children?: React.ReactNode;
}> = ({ open, onClose, title, doc, children }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shown = useRef<string | null>(null);
  // The model is rebuilt on every render; only a real change redraws.
  const key = open && doc ? JSON.stringify(doc) : null;

  useEffect(() => {
    if (!key || !doc) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setDrawing(true);
      try {
        const next = URL.createObjectURL(await pdfBlob(doc));
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        if (shown.current) URL.revokeObjectURL(shown.current);
        shown.current = next;
        setUrl(next);
        setError(null);
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? "The PDF could not be drawn.");
      } finally {
        if (!cancelled) setDrawing(false);
      }
    }, shown.current ? REDRAW_MS : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `key` stands for `doc`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(
    () => () => {
      if (shown.current) URL.revokeObjectURL(shown.current);
    },
    [],
  );

  const download = () => {
    if (!url || !doc) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = doc.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { height: "92vh" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
        <PictureAsPdfOutlined color="action" />
        {title}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, p: 2 }}>
        <Stack spacing={2} sx={{ width: { xs: "100%", md: 340 }, flexShrink: 0, overflowY: "auto" }}>
          {children}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" size="large" startIcon={<FileDownloadOutlined />} disabled={!url || drawing} onClick={download}>
            Download PDF
          </Button>
          {doc && (
            <Typography variant="caption" color="text.secondary">
              Saves as “{doc.fileName}”. The preview is the file itself.
            </Typography>
          )}
        </Stack>
        <Box sx={{ flex: 1, minHeight: 420, position: "relative", bgcolor: "action.hover", borderRadius: 1, overflow: "hidden" }}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
          {url && <Box component="iframe" title={`${title} preview`} src={url} sx={{ border: 0, width: "100%", height: "100%" }} />}
          {(drawing || !url) && !error && (
            <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
              <CircularProgress size={32} />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
